import React from 'react';
import { useSelector } from 'react-redux';
import { DashboardOutlined } from '@ant-design/icons';
import { selectAuth } from '../../store/authSlice';
import { ROLE_NAME_LABELS } from '../../Constants/enums';

const greetingFor = (hour) =>
  hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

const initialsOf = (first, last, email) => {
  const a = (first || '').trim();
  const b = (last || '').trim();
  if (a || b) return `${a[0] || ''}${b[0] || ''}`.toUpperCase();
  return (email || '?').trim()[0].toUpperCase();
};

/**
 * VelorHR-style green header band, used as the consistent header on every page.
 * Two modes:
 *   - Title (pass `title`): page title + subtitle with the page icon on the left.
 *     Used by list pages so their header matches the dashboards.
 *   - Greeting (default): personal greeting + live date, user initials on the left.
 *     Used by the CIO / CFO dashboards.
 * `extra` renders controls on the right (primary action, currency picker, ...).
 */
const WelcomeBanner = ({ title, subtitle, icon, extra }) => {
  const { user, roles } = useSelector(selectAuth);

  if (title) {
    return (
      <div className="welcome-banner">
        {icon ? <div className="wb-avatar">{icon}</div> : null}
        <div className="wb-text">
          <div className="wb-greeting">{title}</div>
          {subtitle ? <div className="wb-sub">{subtitle}</div> : null}
        </div>
        {extra ? <div className="wb-extra">{extra}</div> : null}
      </div>
    );
  }

  const now = new Date();
  const name =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'there';
  const roleText = (roles || []).map((r) => ROLE_NAME_LABELS[r] || r).join(', ');
  const dateText = now.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="welcome-banner">
      <div className="wb-avatar">{initialsOf(user?.firstName, user?.lastName, user?.email)}</div>
      <div className="wb-text">
        <div className="wb-greeting">
          {greetingFor(now.getHours())}, {name}
        </div>
        <div className="wb-sub">{[roleText, dateText].filter(Boolean).join(' · ')}</div>
      </div>
      {extra ? <div className="wb-extra">{extra}</div> : null}
      <div className="wb-badge">{icon || <DashboardOutlined />}</div>
    </div>
  );
};

export default WelcomeBanner;
